/*
  Eluraskin — market-aware prices.

  Two kinds of price live on the PDP outside the buy box:

  - Reference amounts (competitor masks, gift values). Authored once as plain USD
    numbers, rendered by Liquid in the store's default currency, and converted here
    into whatever currency the visitor's Shopify market presents.
  - The product's own price, echoed into the comparison table and body copy. Rendered
    by Liquid from the market price and mirrored here whenever the buy box changes it,
    so a variant switch moves every echo on the page at once.

  Nothing runs when the visitor is already browsing in the store's default currency —
  Liquid has already produced the right numbers in that case.
*/
(() => {
  if (window.EluraskinMoney) return;

  const REFERENCE_SELECTOR = '[data-elura-money]';
  const ECHO_SELECTOR = '[data-elura-market-price]';
  const SOURCE_SELECTOR = '.pdp-price__current';

  const currency = () => {
    const active = window.Shopify && window.Shopify.currency;
    const rate = parseFloat(active && active.rate);
    return {
      code: (active && active.active) || null,
      rate: Number.isFinite(rate) && rate > 0 ? rate : 1,
    };
  };

  const locale = () =>
    document.documentElement.lang || (window.Shopify && window.Shopify.locale) || 'en';

  const format = (amount, code) => {
    try {
      return new Intl.NumberFormat(locale(), {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch (error) {
      return null;
    }
  };

  /*
    A converted anchor must never read like a rate — €689.17 undoes the work the round
    number was doing. Always round up to the next multiple of 5, so an anchor only ever
    moves in our favour. The step grows with the magnitude of the amount, because 5 is
    the wrong grain once a currency runs in the thousands (¥67,005 is the same problem
    in a different shape); for USD, EUR and GBP the step is always plain 5.
  */
  const ROUND_UP_TO = 5;

  const roundUp = (amount) => {
    if (!(amount > 0)) return 0;
    const magnitude = Math.max(0, Math.floor(Math.log10(amount)) - 2);
    const step = ROUND_UP_TO * 10 ** magnitude;
    /* The epsilon keeps an amount that already sits on a step from jumping a whole one. */
    return Math.ceil(amount / step - 1e-9) * step;
  };

  const convertReference = (node, code, rate) => {
    const cents = parseInt(node.getAttribute('data-elura-money'), 10);
    if (!Number.isFinite(cents)) return;

    if (node.dataset.eluraMoneyRendered === String(rate)) return;
    if (!node.dataset.eluraMoneyFallback) node.dataset.eluraMoneyFallback = node.textContent;

    const formatted = format(roundUp((cents * rate) / 100), code);
    node.textContent = formatted || node.dataset.eluraMoneyFallback;
    node.dataset.eluraMoneyRendered = String(rate);
  };

  const scopeOf = (root) =>
    root && typeof root.querySelectorAll === 'function' ? root : document;

  /* Reference amounts: convert store-currency renders into the market currency. */
  const refreshReferences = (root) => {
    const { code, rate } = currency();
    if (!code || rate === 1) return;
    scopeOf(root)
      .querySelectorAll(REFERENCE_SELECTOR)
      .forEach((node) => convertReference(node, code, rate));
  };

  /* Product price echoes: follow the buy box so a variant change moves all of them. */
  const syncEchoes = () => {
    const source = document.querySelector(SOURCE_SELECTOR);
    const price = source && source.textContent.trim();
    if (!price) return;

    document.querySelectorAll(ECHO_SELECTOR).forEach((node) => {
      if (node.textContent.trim() !== price) node.textContent = price;
    });
  };

  let sourceObserver = null;

  const watchSource = () => {
    const source = document.querySelector(SOURCE_SELECTOR);
    if (!source || source === (sourceObserver && sourceObserver.source)) return;

    if (sourceObserver) sourceObserver.observer.disconnect();

    const observer = new MutationObserver(syncEchoes);
    observer.observe(source, { childList: true, characterData: true, subtree: true });
    sourceObserver = { source, observer };
  };

  const refresh = (root) => {
    refreshReferences(root);
    watchSource();
    syncEchoes();
  };

  window.EluraskinMoney = { refresh };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refresh(), { once: true });
  } else {
    refresh();
  }

  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
})();
